import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu as MenuIcon, Phone, X } from "lucide-react";
import { SITE } from "../data/menu";
import { useSettings } from "../site-settings";
import { Blossom, PetalField } from "./decor";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/menu", label: "Menu" },
  { to: "/gallery", label: "Gallery" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { menuVisible } = useSettings();
  const links = LINKS.filter((l) => menuVisible || l.to !== "/menu");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`sticky top-0 z-50 border-b transition-[background-color,box-shadow] duration-500 ${
          scrolled
            ? "border-[#E8B4B8]/40 bg-[#F8E8E8]/90 shadow-[0_8px_30px_rgba(155,44,44,0.06)] backdrop-blur-xl"
            : "border-transparent bg-[#F8E8E8]/70 backdrop-blur-md"
        }`}
      >
        <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:px-10">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5">
            <img src="/images/logo.webp" alt="Bloomora logo" className="h-9 w-9 rounded-full object-cover shadow-sm" />
            <span className="font-serif text-2xl font-bold tracking-wide text-[#9B2C2C]">BLOOMORA</span>
            <span className="font-hindi hidden text-sm text-[#B86B77] sm:inline">ब्लूमोरा</span>
          </Link>

          <ul className="hidden items-center gap-8 lg:flex">
            {links.map((l) => (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `group relative py-1 text-sm font-medium tracking-wide transition-colors ${
                      isActive ? "text-[#9B2C2C]" : "text-[#2C2C2C] hover:text-[#9B2C2C]"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {l.label}
                      <span
                        className={`absolute -bottom-0.5 left-1/2 h-[2px] -translate-x-1/2 bg-[#C9A227] transition-[width] duration-300 ${
                          isActive ? "w-full" : "w-0 group-hover:w-full"
                        }`}
                      />
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-3">
            <Link
              to="/order"
              data-testid="nav-order-online"
              className="relative hidden items-center gap-2 rounded-full border-2 border-[#C9A227] px-5 py-2 text-sm font-semibold text-[#9B2C2C] shadow-[0_0_18px_rgba(201,162,39,0.4)] transition-all hover:bg-[#C9A227] hover:text-white md:flex"
            >
              Order Online
              <span className="absolute -right-2 -top-2 rounded-full bg-[#C9A227] px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide text-white shadow">
                Fast Delivery
              </span>
            </Link>
            <a
              href={SITE.phoneHref}
              data-testid="nav-phone-number"
              className="hidden text-sm font-medium text-[#9B2C2C] lg:block"
            >
              {SITE.phone}
            </a>
            <a
              href={SITE.phoneHref}
              data-testid="nav-call-now-button"
              className="group relative hidden items-center gap-2 rounded-full bg-[#9B2C2C] px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#7E2222] sm:flex"
            >
              <Phone size={15} />
              Call Now
              <span className="absolute bottom-1.5 left-1/2 h-[2px] w-0 -translate-x-1/2 bg-[#C9A227] transition-[width] duration-300 group-hover:w-1/2" />
            </a>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(true)}
              className="rounded-full p-2 text-[#9B2C2C] transition-colors hover:bg-[#E8B4B8]/30 lg:hidden"
              aria-label="Open menu"
            >
              <MenuIcon size={24} />
            </button>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu-overlay"
            className="fixed inset-0 z-[60] flex flex-col bg-[#F8E8E8]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            <PetalField count={10} />
            <div className="flex items-center justify-between px-5 py-4">
              <span className="font-serif text-2xl font-bold text-[#9B2C2C]">BLOOMORA</span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="rounded-full p-2 text-[#9B2C2C] hover:bg-[#E8B4B8]/30"
                aria-label="Close menu"
              >
                <X size={26} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col items-center justify-center gap-2">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.07, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 py-3 font-serif text-4xl transition-colors ${
                        isActive ? "italic text-[#9B2C2C]" : "text-[#2C2C2C] hover:text-[#9B2C2C]"
                      }`
                    }
                  >
                    <Blossom className="h-6 w-6 text-[#E8B4B8]" />
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 + links.length * 0.07, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              >
                <NavLink
                  to="/order"
                  data-testid="mobile-nav-link-order"
                  className="mt-3 flex items-center gap-2 rounded-full bg-[#9B2C2C] px-8 py-3 font-serif text-2xl text-white transition-colors hover:bg-[#7A1D28]"
                >
                  <Blossom className="h-5 w-5 text-[#C9A227]" />
                  Order Online
                  <span className="rounded-full bg-[#C9A227] px-2 py-0.5 font-sans text-[10px] font-bold uppercase tracking-wide text-white">
                    Fast Delivery
                  </span>
                </NavLink>
              </motion.div>
            </nav>
            <div className="pb-10 text-center">
              <a
                href={SITE.phoneHref}
                data-testid="mobile-menu-call-button"
                className="inline-flex items-center gap-2 rounded-full bg-[#9B2C2C] px-8 py-3 font-medium text-white"
              >
                <Phone size={16} /> {SITE.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
